'use client'
import type { ImageData, Maybe } from '@/frontend-types'
import type { FC } from 'react'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'
import { IconCircle } from '../atoms/IconCircle'
import { ImageWithCorner } from '../atoms/ImageWithCorner'
import { bulletActiveClass, bulletClass } from '../molecules/CarouselCardImages'
import ContentBlockWrapper from '../molecules/ContentBlockWrapper'

// Import Swiper styles
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { TextAreaContent } from '../atoms/TextAreaContent'

export type ImageGalleryType = {
	id: Maybe<string>
	image?: ImageData
	caption: Maybe<string>
}

export type CarouselImageGalleryBlockProps = {
	images: ImageGalleryType[]
}

export const CarouselImageGalleryBlock: FC<CarouselImageGalleryBlockProps> = ({ images }) => {
	return (
		<ContentBlockWrapper blockTypes={['component']} className="overflow-hidden">
			<div className="relative col-span-full">
				<Swiper
					modules={[Navigation, Pagination]}
					spaceBetween={16}
					slidesPerView={1}
					navigation={{
						prevEl: '.swiper-button-prev-custom',
						nextEl: '.swiper-button-next-custom',
					}}
					pagination={{
						clickable: true,
						el: '.swiper-pagination-custom',
						bulletClass: bulletClass,
						bulletActiveClass: bulletActiveClass,
					}}
					breakpoints={{
						1024: {
							slidesPerView: 1.1,
							spaceBetween: 16,
						},
						1280: {
							slidesPerView: 1.2,
							spaceBetween: 24,
						},
					}}
					watchSlidesProgress={true}
					className="!overflow-visible"
				>
					{images.map((image) => (
						<SwiperSlide key={image.id}>
							<ImageWithCorner
								image={image.image}
								aspectRatio="aspect-image-gallery"
								sizes="(max-width: 767px) 100vw, (max-width: 1023px) 90vw, (max-width: 1559px) 80vw, 1180px"
							/>
							<TextAreaContent className="mt-sm" text={image.caption} textSize="text-body-xs" />
						</SwiperSlide>
					))}

					{/* Custom Navigation Buttons */}
					<button
						className="swiper-button-prev-custom group pointer-events-auto absolute top-1/2 left-0 z-10 flex translate-x-8 cursor-pointer items-center justify-center rounded-full disabled:pointer-events-none disabled:opacity-0"
						aria-label="Previous"
					>
						<IconCircle icon={['fal', 'chevron-left']} size="sm" theme="blue" />
					</button>

					<button
						className="swiper-button-next-custom group pointer-events-auto absolute top-1/2 right-0 z-10 flex -translate-x-8 cursor-pointer items-center justify-center rounded-full disabled:pointer-events-none disabled:opacity-0"
						aria-label="Next"
					>
						<IconCircle icon={['fal', 'chevron-right']} size="sm" theme="blue" />
					</button>
				</Swiper>
			</div>

			{/* Custom Pagination */}
			<div className="swiper-pagination-custom col-span-full mt-sm flex justify-center gap-2"></div>
		</ContentBlockWrapper>
	)
}

export default CarouselImageGalleryBlock
